import ListItem from '@components/common/Header/CartListItem'
import { CategoriesView } from '@components/common/Header/CategoriesView'
import { IconTallink } from '@components/common/Header/HeaderIconTallink'
import { LanguageSelector } from '@components/common/Header/LanguageSelector'
import PriceListItem from '@components/common/Header/PriceListItem'
import { UpperNavigation } from '@components/common/Header/UpperNavigation'
import {
  CwsA,
  CwsAlert,
  CwsButton,
  CwsDivider,
  CwsGridCol,
  CwsGridRow,
  CwsHeader,
  CwsHeaderLogo,
  CwsHeaderMenu,
  CwsHeaderMenuNav,
  CwsHeaderMenuNavItem,
  CwsHeaderProfile,
  CwsHeaderProfileContent,
  CwsHeaderProfileNavigation,
  CwsHeaderProfileNavigationItem,
  CwsHeaderShopping,
  CwsHeaderShoppingHeader,
  CwsHeaderShoppingList,
  CwsHeaderWrapper,
  CwsP,
  CwsSection,
} from '@components/cws'
import type { BundleInfo } from '@components/profile/Wishlist/Wishlist'
import { useInitialData } from '@context/initialDataContext'
import { useAppState } from '@context/state'
import { useCart } from '@hooks/useCart'
import { useCurrency } from '@hooks/useCurrency'
import { useTranslate } from '@hooks/useTranslation'
import { useWishlist } from '@hooks/useWishlist'
import { useWishListPriceInfos } from '@hooks/useWishListPriceInfos'

import type { Locale } from '@model/locales'

import { getFormattedPrice } from '@utils/price'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'

import { useRouter } from 'next/dist/client/router'
import Link from 'next/link'
import * as R from 'ramda'
import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SSOContainer } from '@components/common/Header/SSOContainer'
import { HeaderSearch } from '@components/common/Header/HeaderSearch'
import { IntegratedMobileHeader } from '@components/common/Header/IntegratedMobileHeader'
import Tooltip from '../../../ui/components/Tooltip/Tooltip'
import { developBranchName } from '@utils/developBranchName'
import { isPrintMode } from '@utils/is-print-mode'
import { device600, device800, useResize } from '@ui/hooks/useResize'
import { freeProductsFactory } from '@components/profile/Wishlist/Wishlist'
import { useCartListPriceInfos } from '@hooks/useCartListPriceInfos'

export const IntegratedHeader: FC = () => {
  const { locale, push, pathname, query, asPath } = useRouter()
  const { deviceSize } = useResize()
  const isMobileSso = deviceSize < device800
  const isMobile = deviceSize < device600

  const { translate } = useTranslate()
  const { currency } = useCurrency()
  const { itemsCount, cart = [], isLoading } = useCart('shopping cart')
  const { itemsCount: wishlistCount, wishlist = [], totalPrice: wishlistTotalPrice } = useWishlist()
  const [totalPrices, setTotalPrices] = useState({})
  const [totalPricesWishlist, setTotalPricesWishlist] = useState({})
  const wishlistItems = wishlist.length > 3 ? wishlist.slice(0, 3) : wishlist

  const setTotalCartPrices = useCallback(
    (itemSku: string, price: number) => {
      const listSku = R.map(R.prop('sku'), cart)
      setTotalPrices((prevState) => {
        const filteredData = R.pick(listSku, prevState)
        return { ...filteredData, [itemSku]: price }
      })
    },
    [cart]
  )
  const setTotalWishlistPrices = useCallback((itemSku: string, price: number) => {
    setTotalPricesWishlist((prevState) => {
      return { ...prevState, [itemSku]: price }
    })
  }, [])
  const totalPriceWishlist: number = useMemo(() => {
    return R.sum(Object.values(totalPricesWishlist))
  }, [totalPricesWishlist])
  const totalPriceCart: number = useMemo(() => {
    return R.sum(Object.values(totalPrices))
  }, [totalPrices])

  const bundleInfosWishList = useWishListPriceInfos()
  const bundleInfosCart = useCartListPriceInfos()

  const bundleGroupsWishList = R.groupBy<BundleInfo>(R.prop('bundleLabel'))(bundleInfosWishList)
  const bundleGroupsCart = R.groupBy<BundleInfo>(R.prop('bundleLabel'))(bundleInfosCart)

  const freeProductsWishList = useMemo(freeProductsFactory(bundleGroupsWishList, currency), [
    bundleGroupsWishList,
    wishlist,
  ])
  const freeProductsCart = useMemo(freeProductsFactory(bundleGroupsCart, currency), [bundleGroupsCart, cart])

  const canShowWishlist = useMemo(() => {
    if (isGloballyPricelist()) {
      return Boolean(query.catalogId) && Boolean(wishlistCount)
    }
    return Boolean(wishlistCount)
  }, [query, wishlistCount])
  const {
    state: { customer },
  } = useAppState()

  const {
    initialData: { currencies, categories, catalogs },
  } = useInitialData()

  useEffect(() => {
    if (customer) {
      // reload the wishlist and cart
    }
  }, [customer])

  const openLegacyBasket = useCallback(() => {
    if (itemsCount) push('/cart').catch(console.error)
  }, [itemsCount, push])

  const openLegacyWishlist = useCallback(() => {
    if (wishlistCount)
      push(isGloballyPricelist() && query.catalogId ? `/${query.catalogId}/shopping-list` : `/my-wishlist`).catch(
        console.error
      )
  }, [wishlistCount, push, query])

  const productListCountRef = useRef<number>(0)

  const productListCount = useMemo(() => {
    if (isLoading) return productListCountRef.current
    const count =
      cart?.reduce((acc, cur) => {
        return cur.quantity + acc
      }, 0) ?? 0
    productListCountRef.current = count

    return count
  }, [isLoading, cart])
  const isPrint = isPrintMode()
  const shoppingListLink =
    isGloballyPricelist() && query.catalogId ? `/${query.catalogId}/shopping-list` : '/my-wishlist'

  const handleOpenSso = (event: MouseEvent) => {
    event.stopPropagation()
    window.SSO.open()
  }

  if (isMobile) {
    return (
      <IntegratedMobileHeader
        freeProductsWishList={freeProductsWishList}
        freeProductsCart={freeProductsCart}
        productListCount={productListCount}
        cart={cart}
        itemsCount={cart.length}
        currency={currency}
        currencies={currencies}
        setTotalCartPrices={setTotalCartPrices}
        totalPriceCart={totalPriceCart}
        wishlist={wishlist}
        openLegacyWishlist={openLegacyWishlist}
        setTotalWishlistPrices={setTotalWishlistPrices}
        totalPriceWishlist={totalPriceWishlist}
        bundleGroupsWishList={bundleGroupsWishList}
        bundleGroupsCart={bundleGroupsCart}
        shoppingListLink={shoppingListLink}
        categories={categories}
        openLegacyBasket={openLegacyBasket}
        catalogs={catalogs}
      />
    )
  }
  return (
    <>
      {isPrint && (
        <CwsGridRow>
          <CwsGridCol col="12">
            <CwsHeaderLogo>
              <Link passHref href="/" legacyBehavior>
                <CwsA hideArrow locale={locale}>
                  <IconTallink isPriceListApp={isGloballyPricelist()} />
                </CwsA>
              </Link>
            </CwsHeaderLogo>
          </CwsGridCol>
        </CwsGridRow>
      )}
      <CwsHeaderWrapper mobile={isMobile}>
        {isMobileSso && <SSOContainer locale={locale || 'et'} />}
        <CwsHeader>
          <UpperNavigation />

          <CwsSection className="cws-align-items-flex-start cws-d-flex" variant="div">
            <CwsSection className="cws-d-flex cws-mr-32-md" variant="div">
              <LanguageSelector
                selectedLanguageCode={locale ?? 'en'}
                onSelectLanguage={(locale: Locale) => push({ pathname, query }, asPath, { locale })}
              />
              {((!isMobile && canShowWishlist) || (!isMobile && Boolean(itemsCount))) && (
                <CwsDivider className="cws-hidden-xs-sm" color="dark-grey" orientation="vertical" spacing="s" />
              )}
              <>
                {canShowWishlist && (
                  <CwsHeaderShopping className="cws-hidden-xs-sm cws-mr-24-md" tagNotification={Boolean(wishlistCount)}>
                    <CwsHeaderShoppingHeader
                      title={translate('wishlist.tile.wishlist')}
                      icon="wishlist"
                      count={
                        wishlist?.reduce((acc, cur) => {
                          return cur.quantityDesired + acc
                        }, 0) ?? 0
                      }
                    >
                      <Link href={shoppingListLink} passHref legacyBehavior>
                        <CwsA color="buoy-link">{translate('wishlist.view.all')}</CwsA>
                      </Link>
                    </CwsHeaderShoppingHeader>

                    <CwsHeaderShoppingList
                      price={getFormattedPrice(totalPriceWishlist, currency)}
                      moreItemsLabel={
                        wishlist.length > 3 ? translate('wishlist.more.items', { count: wishlist.length - 3 }) : ''
                      }
                    >
                      {wishlist.map((item, idx) =>
                        item.product ? (
                          <PriceListItem
                            bundleGroups={bundleGroupsWishList}
                            key={item.id}
                            item={item}
                            currency={currency}
                            quantityProductIsFree={(freeProductsWishList[item.product.sku] as number) || 0}
                            isWishlist
                            setTotalCartPrices={setTotalWishlistPrices}
                            elementIndex={idx}
                          />
                        ) : null
                      )}
                    </CwsHeaderShoppingList>
                    <CwsSection className="cws-p-xs" variant="div">
                      <CwsButton width="fluid" onClick={openLegacyWishlist}>
                        {translate('wishlist.view.all')}
                      </CwsButton>
                    </CwsSection>
                    <CwsAlert className="hidden">
                      <CwsP hideSpacing size="small" weight="500">
                        Delivery to chosen destination is free! [translate]
                      </CwsP>
                    </CwsAlert>
                  </CwsHeaderShopping>
                )}
                {Boolean(itemsCount) && (
                  <CwsHeaderShopping
                    className="cws-mr-24-md"
                    icon="shopping"
                    color="write"
                    tagNotification={Boolean(itemsCount)}
                  >
                    <CwsHeaderShoppingHeader
                      count={productListCount}
                      title={translate('cart.tile.shoppingcart')}
                      icon="shopping"
                    >
                      <Link passHref href="/cart" legacyBehavior>
                        <CwsA color="buoy-link">{translate('wishlist.view.all')}</CwsA>
                      </Link>
                    </CwsHeaderShoppingHeader>
                    <CwsHeaderShoppingList
                      price={getFormattedPrice(totalPriceCart, currency)}
                      moreItemsLabel={cart.length > 3 ? translate('cart.more.items', { count: cart.length - 3 }) : ''}
                    >
                      {cart?.map((item, idx) => {
                        return item.product ? (
                          <ListItem
                            key={item.id}
                            item={item}
                            currency={currency}
                            isWishlist={false}
                            setTotalCartPrices={setTotalCartPrices}
                            quantityProductIsFree={(freeProductsCart[item.product.sku] as number) || 0}
                            bundleGroups={bundleGroupsCart}
                            elementIndex={idx}
                          />
                        ) : null
                      })}
                    </CwsHeaderShoppingList>
                    <CwsSection className="cws-p-xs" variant="div">
                      <CwsButton width="fluid" onClick={openLegacyBasket}>
                        {translate('basket.go.to.checkout')}
                      </CwsButton>
                    </CwsSection>
                  </CwsHeaderShopping>
                )}
              </>
            </CwsSection>
            <CwsHeaderProfile>
              {!isMobileSso && <SSOContainer locale={locale || 'et'} />}
              <CwsHeaderProfileNavigation className="cws-hidden-md">
                <CwsHeaderProfileNavigationItem
                  onClick={handleOpenSso}
                  label={customer ? `${customer.lastName} ${customer.firstName}` : translate('w2.not.customer.text')}
                  mode="sso"
                />

                {canShowWishlist && (
                  <CwsHeaderProfileNavigationItem label={translate('wishlist.tile.wishlist')} mode="wishlist" />
                )}
              </CwsHeaderProfileNavigation>
              <CwsHeaderProfileContent className="cws-hidden-md" mode="wishlist">
                {canShowWishlist && (
                  <CwsHeaderShopping icon="wishlist">
                    <CwsHeaderShoppingHeader
                      title={translate('wishlist.tile.wishlist')}
                      icon="wishlist"
                      count={
                        wishlist?.reduce((acc, cur) => {
                          return cur.quantityDesired + acc
                        }, 0) ?? 0
                      }
                    />

                    <CwsHeaderShoppingList
                      price={getFormattedPrice(totalPriceWishlist, currency)}
                      moreItemsLabel={
                        wishlist.length > 3 ? translate('wishlist.more.items', { count: wishlist.length - 3 }) : ''
                      }
                    >
                      {wishlistItems.map((item) =>
                        item.product && item.quantityDesired ? (
                          <PriceListItem
                            bundleGroups={bundleGroupsWishList}
                            key={item.id}
                            item={item}
                            currency={currency}
                            quantityProductIsFree={(freeProductsWishList[item.product.sku] as number) || 0}
                            isWishlist
                            setTotalCartPrices={setTotalWishlistPrices}
                          />
                        ) : null
                      )}
                    </CwsHeaderShoppingList>
                    <CwsSection className="cws-p-xs" variant="div">
                      <CwsButton width="fluid" onClick={openLegacyWishlist}>
                        {translate('wishlist.view.all')}
                      </CwsButton>
                    </CwsSection>
                    <CwsAlert className="hidden">
                      <CwsP hideSpacing size="small" weight="500">
                        Delivery to chosen destination is free! [translate]
                      </CwsP>
                    </CwsAlert>
                  </CwsHeaderShopping>
                )}
              </CwsHeaderProfileContent>
            </CwsHeaderProfile>
          </CwsSection>
        </CwsHeader>

        <CwsHeaderMenu>
          {!isMobile && (
            <>
              {developBranchName() ? (
                <Tooltip position="bottom" contentCenter="left" notDotted content={developBranchName()}>
                  <CwsHeaderLogo>
                    <Link passHref href="/" legacyBehavior>
                      <CwsA hideArrow locale={locale}>
                        <IconTallink isPriceListApp={isGloballyPricelist()} />
                      </CwsA>
                    </Link>
                  </CwsHeaderLogo>
                </Tooltip>
              ) : (
                <CwsHeaderLogo>
                  <Link passHref href="/" legacyBehavior>
                    <CwsA hideArrow locale={locale}>
                      <IconTallink isPriceListApp={isGloballyPricelist()} />
                    </CwsA>
                  </Link>
                </CwsHeaderLogo>
              )}
            </>
          )}

          {isGloballyPricelist() && !Boolean(categories.length) && Boolean(catalogs.length) && (
            <CwsHeaderMenuNav>
              {catalogs.map((ferry) => (
                <CwsHeaderMenuNavItem key={ferry.id} href={`/${locale}/${ferry.id}`} icon="ship" label={ferry.title}>
                  <CwsA href={`/${locale}/${ferry.id}`} />
                </CwsHeaderMenuNavItem>
              ))}
            </CwsHeaderMenuNav>
          )}
          {categories && <CategoriesView categories={categories} />}
          <HeaderSearch />
        </CwsHeaderMenu>
      </CwsHeaderWrapper>
    </>
  )
}
